/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link, Stack } from '@fluentui/react';
import { Property } from 'csstype';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import { useLocalStore } from 'mobx-react-lite';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { PillContainer } from 'src/core/components/PillContainer';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import {
    CustomTextTestModelsFiltersBlade,
    FilterItem
} from 'src/customText/appShell/modelDetailsPages/components/CustomTextTestModelsFiltersBlade';
import { CustomTextTestSetDetailsTable } from 'src/customText/appShell/modelDetailsPages/components/CustomTextTestSetDetailsTable';
import { TestDocumentDetailsModal } from 'src/customText/appShell/modelDetailsPages/components/TestDocumentDetailsModal';
import { useFetchDocuments } from 'src/customText/core/hooks/useFetchDocuments';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { CustomTextModelVerificationStore } from 'src/customText/core/stores/CustomTextModelVerificationStore';
import { getClassificationTableItems, getExtractionTableItems } from 'src/customText/core/utils/testSetDetailsUtils';
import { TestSetDetailsCommandBar } from 'src/modules/modelGuidance/components/TestSetDetailsCommandBar';

export type CustomTextTestSetDetailsTableRow = {
    name: string;
    text: string;
    rowKey: string;
    labelText: string;
    resultType: string;
    predictionText: string;
    isResultTruePositive: boolean;
    label: CustomTextEntityLabelStore | string;
    prediction: CustomTextEntityLabelStore | string;
};

type CustomTextTestSetDetailsTabProps = {
    isModalOpen?: boolean;
    selectedRowKey?: string;
    selectedModelName: string;
    filteredLabel?: string;
    filteredPrediction?: string;
    showIncorrectPredictionsOnly?: boolean;
};

const messages = defineMessages({
    description: {
        id: 'customText.customTextTestSetDetailsTab.description',
        defaultMessage: `Select {labelType} to review its details including the context of its surrounding text in the document. The information here is static and tied to your model, so it won’t update until you train again. By default, only mismatches of {defaultValues} are shown.`
    },
    noDocumentsErrorMsg: {
        id: 'customText.customTextTestSetDetailsTab.noDocumentsErrorMsg',
        defaultMessage: 'No test set documents available'
    },
    noIncorrectDocumentsErrorMsg: {
        id: 'customText.customTextTestSetDetailsTab.noIncorrectDocumentsErrorMsg',
        defaultMessage: 'No incorrect test set documents available'
    },
    notLabeled: {
        id: 'customText.customTextTestSetDetailsTab.notLabeled',
        defaultMessage: 'Unlabeled in test set'
    },
    notPredicted: {
        id: 'customText.customTextTestSetDetailsTab.notPredicted',
        defaultMessage: 'No prediction'
    },
    truePositive: {
        id: 'customText.customTextTestSetDetailsTab.truePositive',
        defaultMessage: 'True Positive'
    },
    falsePositive: {
        id: 'customText.customTextTestSetDetailsTab.falsePositive',
        defaultMessage: 'False Positive'
    },
    falseNegative: {
        id: 'customText.customTextTestSetDetailsTab.falseNegative',
        defaultMessage: 'False Negative'
    },
    conflict: {
        id: 'customText.customTextTestSetDetailsTab.conflict',
        defaultMessage: 'Conflict'
    },
    noConflict: {
        id: 'customText.customTextTestSetDetailsTab.noConflict',
        defaultMessage: 'No conflict'
    },
    filtersApplied: {
        id: 'customText.customTextTestSetDetailsTab.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAllLabels: {
        id: 'customText.customTextTestSetDetailsTab.clearAllLabels',
        defaultMessage: 'Clear all labels'
    },
    clearAllPredictions: {
        id: 'customText.customTextTestSetDetailsTab.clearAllPredictions',
        defaultMessage: 'Clear all predictions'
    },
    clearAllResults: {
        id: 'customText.customTextTestSetDetailsTab.clearAllResults',
        defaultMessage: 'Clear all results'
    }
});

const filtersTheme = getScopedTheme('Filters');
const { styleDiv } = getStylist('CustomTextTestSetDetailsTab');

const ModelsTableWindow = styleDiv('ModelsTableWindow', { height: '100%', overflowY: 'auto' });

export const CustomTextTestSetDetailsTab = observer((props: CustomTextTestSetDetailsTabProps) => {
    const {
        selectedModelName,
        isModalOpen = false,
        filteredLabel,
        filteredPrediction,
        selectedRowKey,
        showIncorrectPredictionsOnly = true
    } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const entityStores = rootStore.currentCustomTextProjectStore.extractionStore?.entities ?? [];
    const classStores = rootStore.currentCustomTextProjectStore.classificationStore?.classes ?? [];

    const models = rootStore.currentCustomTextProjectStore.trainedModels.items ?? [];
    const isInEmptyState = models.length === 0;

    const [showIncorrectPredictions, setShowIncorrectPredictions] = useState<boolean>(
        filteredLabel && filteredPrediction ? false : showIncorrectPredictionsOnly
    );
    const selectedModel = rootStore.currentCustomTextProjectStore.trainedModels.items.find(model => model.name === selectedModelName);

    const modelVerificationStore = useLocalStore<CustomTextModelVerificationStore>(
        () => new CustomTextModelVerificationStore(selectedModelName)
    );

    const isExtractionProject =
        rootStore.currentCustomTextProjectStore.projectKind === 'CustomEntityRecognition' ||
        rootStore.currentCustomTextProjectStore.projectKind === 'CustomHealthcare';

    const [sortKey, setSortKey] = React.useState<SortKeyType<CustomTextTestSetDetailsTableRow>>({ key: 'name', order: 'asc' });

    const getRightLabelName = (name: string) =>
        name === LuisConstants.CustomText.NoneLabelInConfusionMatrix ? localize(messages.notLabeled) : name;
    const getRightPredictionName = (name: string) =>
        name === LuisConstants.CustomText.NoneLabelInConfusionMatrix ? localize(messages.notPredicted) : name;

    const labels = React.useMemo(
        () =>
            isExtractionProject
                ? entityStores.filter(entity => entity.prebuilts.length === 0).map(entity => entity.name)
                : classStores.map(_class => _class.name),
        [isExtractionProject, entityStores, classStores]
    );
    const resultTypes = React.useMemo(
        () =>
            isExtractionProject
                ? [localize(messages.falsePositive), localize(messages.falseNegative), localize(messages.truePositive)]
                : [localize(messages.conflict), localize(messages.noConflict)],
        [isExtractionProject]
    );

    const verificationDataTracker = useProgressTracker();

    const tableRef = React.createRef<HTMLDivElement>();
    const [searchQuery, setSearchQuery] = React.useState('');
    const [isFilterBladeHidden, setIsFilterBladeHidden] = React.useState(true);
    const [selectedTableRowIndex, setSelectedTableRowIndex] = React.useState(-1);
    const [isSelectedFileModalHidden, setIsSelectedFileModalHidden] = React.useState(!isModalOpen);

    const [labelsToFilter, updateLabelsToFilter] = React.useState<FilterItem[]>(
        [localize(messages.notLabeled), ...labels].map(label => ({
            itemName: label,
            isChecked: label === getRightLabelName(filteredLabel)
        }))
    );
    const [predictionToFilter, updatePredictionsToFilter] = React.useState<FilterItem[]>(
        [localize(messages.notPredicted), ...labels].map(label => ({
            itemName: label,
            isChecked: label === getRightPredictionName(filteredPrediction)
        }))
    );
    const [resultsToFilter, updateResultsToFilter] = React.useState<FilterItem[]>(
        resultTypes.map(result => ({ itemName: result, isChecked: false }))
    );

    const getCheckedItemNames = (items: FilterItem[]) => items.filter(item => item.isChecked).map(item => item.itemName);

    const isTableRowInFilteredItems = (tableRow: CustomTextTestSetDetailsTableRow) => {
        const filteredLabels = getCheckedItemNames(labelsToFilter);
        const filteredResults = getCheckedItemNames(resultsToFilter);
        const filteredPredictions = getCheckedItemNames(predictionToFilter);

        const areLabelsExistInFilteredItems = (filteredItems: string[], labelText: string) => {
            const labeledWords = isExtractionProject ? [labelText] : labelText.split(',');

            return labeledWords.some(item => filteredItems.includes(item.trim()));
        };

        if (
            (filteredLabels.length > 0 && !areLabelsExistInFilteredItems(filteredLabels, tableRow.labelText)) ||
            (filteredResults.length > 0 && !filteredResults.includes(tableRow.resultType)) ||
            (filteredPredictions.length > 0 && !areLabelsExistInFilteredItems(filteredPredictions, tableRow.predictionText))
        ) {
            return false;
        }

        return true;
    };

    const allPredictions = React.useMemo(
        () =>
            showIncorrectPredictions
                ? modelVerificationStore.documents.filter(document => document.hasConflicts)
                : modelVerificationStore.documents,
        [showIncorrectPredictions, modelVerificationStore?.documents?.length]
    );

    const tableItems = isExtractionProject
        ? getExtractionTableItems(
              localize(messages.notLabeled),
              localize(messages.notPredicted),
              localize(messages.truePositive),
              localize(messages.falsePositive),
              localize(messages.falseNegative),
              entityStores,
              allPredictions,
              showIncorrectPredictions
          )
        : getClassificationTableItems(
              localize(messages.notLabeled),
              localize(messages.notPredicted),
              localize(messages.conflict),
              localize(messages.noConflict),
              allPredictions,
              showIncorrectPredictions
          );

    const filteredItems = React.useMemo(
        () =>
            orderBy(
                substringSearchStrategy(tableItems, searchQuery, ['name', 'text', 'labelText', 'predictionText', 'resultType']).filter(
                    isTableRowInFilteredItems
                ),
                [createKeyedStringNormalizer(sortKey.key)],
                [sortKey.order]
            ),
        [searchQuery, tableItems, sortKey.order, sortKey.key]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(
        filteredItems,
        LuisConstants.ResourceLimits.DefaultPageSize
    );

    const { isLoading: areDocumentsLoading } = useFetchDocuments(
        allPredictions.filter(item => pageItems.find(pageItem => pageItem.name === item.name))
    );
    const areThereSomeFilters = () =>
        labelsToFilter.filter(item => item.isChecked).length ||
        predictionToFilter.filter(item => item.isChecked).length ||
        resultsToFilter.filter(item => item.isChecked).length;

    const updateFilteredItems = (newLabels: FilterItem[], newPredictions: FilterItem[], newResults: FilterItem[]) => {
        updateLabelsToFilter(newLabels);
        updateResultsToFilter(newResults);
        updatePredictionsToFilter(newPredictions);
    };

    const { labelType, defaultValues } = React.useMemo(
        () =>
            isExtractionProject
                ? { labelType: 'an entity', defaultValues: 'false positives and false negatives' }
                : { labelType: 'a class', defaultValues: 'conflict' },
        [isExtractionProject]
    );

    const onRemoveLabelFilter = (itemName: string) => {
        updateLabelsToFilter(labelsToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));
    };

    const onClearLabelFilters = () => {
        updateLabelsToFilter(labelsToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));
    };

    const onRemovePredictionFilter = (itemName: string) => {
        updatePredictionsToFilter(predictionToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));
    };

    const onClearPredictionFilters = () => {
        updatePredictionsToFilter(predictionToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));
    };

    const onRemoveResultFilter = (itemName: string) => {
        updateResultsToFilter(resultsToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));
    };

    const onClearResultFilters = () => {
        updateResultsToFilter(resultsToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));
    };

    const getPlaceholder = () => {
        if (showIncorrectPredictions) {
            return localize(messages.noIncorrectDocumentsErrorMsg);
        }

        return localize(messages.noDocumentsErrorMsg);
    };

    React.useEffect(
        () => () => {
            onClearLabelFilters();
            onClearResultFilters();
            onClearPredictionFilters();
            setShowIncorrectPredictions(showIncorrectPredictionsOnly);
        },
        []
    );

    React.useEffect(() => {
        if (selectedTableRowIndex === -1) {
            pageItems.map((item, index) => {
                if (item.rowKey === selectedRowKey) {
                    setSelectedTableRowIndex(index);
                }
            });
        }
    }, [selectedRowKey, currentPage]);

    React.useEffect(() => {
        if (rootStore.currentCustomTextProjectStore && selectedModel) {
            dispatcher.dispatch('getTrainedModelVerificationData', {
                trainedModelName: selectedModelName,
                localStore: modelVerificationStore,
                tracker: verificationDataTracker,
                toastData: {}
            });
        }
    }, [selectedModelName, rootStore.currentCustomTextProjectStore, selectedModel]);

    const FilteredItemPills = (_props: {
        onClearFilters: () => void;
        clearFilteredItemsText: string;
        filteredItemsToShow: FilterItem[];
        onRemoveFilter: (itemName: string) => void;
    }) => {
        const { filteredItemsToShow, onRemoveFilter, onClearFilters, clearFilteredItemsText } = _props;
        return getCheckedItemNames(filteredItemsToShow).length ? (
            <Stack horizontal>
                <PillContainer
                    allowAddPills={false}
                    pills={filteredItemsToShow
                        .filter(item => item.isChecked)
                        .map((item, index) => ({
                            index,
                            name: item.itemName,
                            pillKey: `${item.itemName}-${index}`,
                            onRemovePill: () => onRemoveFilter(item.itemName),
                            onRenderPillContent: () => (
                                <LuisText title={item.itemName} fontWeight={filtersTheme.boldFontWeight as Property.FontWeight}>
                                    {item.itemName}
                                </LuisText>
                            )
                        }))}
                />
                <Link as="button" onClick={() => onClearFilters()}>
                    <span aria-label={clearFilteredItemsText}>{clearFilteredItemsText}</span>
                </Link>
            </Stack>
        ) : null;
    };

    const getFilteredLabels = () => (
        <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
            <LuisText aria-label={localize(messages.filtersApplied)}>{localize(messages.filtersApplied)}</LuisText>

            <Stack>
                <FilteredItemPills
                    filteredItemsToShow={labelsToFilter}
                    onRemoveFilter={onRemoveLabelFilter}
                    onClearFilters={onClearLabelFilters}
                    clearFilteredItemsText={localize(messages.clearAllLabels)}
                />
                <FilteredItemPills
                    filteredItemsToShow={predictionToFilter}
                    onRemoveFilter={onRemovePredictionFilter}
                    onClearFilters={onClearPredictionFilters}
                    clearFilteredItemsText={localize(messages.clearAllPredictions)}
                />
                <FilteredItemPills
                    filteredItemsToShow={resultsToFilter}
                    onRemoveFilter={onRemoveResultFilter}
                    onClearFilters={onClearResultFilters}
                    clearFilteredItemsText={localize(messages.clearAllResults)}
                />
            </Stack>
        </Stack>
    );

    const areItemsFiltered = () => labelsToFilter.length > 0 || predictionToFilter.length > 0 || resultsToFilter.length > 0;

    return (
        <Page size="container-fluid" pageName="CustomTextTestSetDetailsTab">
            <Stack tokens={{ childrenGap: 10 }} verticalFill>
                <TestSetDetailsCommandBar
                    query={searchQuery}
                    isInEmptyState={isInEmptyState}
                    onQueryChange={value => setSearchQuery(value)}
                    onFilter={() => setIsFilterBladeHidden(false)}
                    showIncorrectPredictions={showIncorrectPredictions}
                    onChangeShowIncorrectPredictions={() => setShowIncorrectPredictions(!showIncorrectPredictions)}
                />

                <LuisText>{localize(messages.description, { labelType, defaultValues })}</LuisText>

                {areThereSomeFilters() && getFilteredLabels()}

                {isInEmptyState ? (
                    <EmptyStateView />
                ) : (
                    <div ref={tableRef}>
                        <ModelsTableWindow>
                            <CustomTextTestSetDetailsTable
                                sortKey={sortKey}
                                items={pageItems}
                                entityStores={entityStores}
                                emptyPlaceholder={getPlaceholder()}
                                overallItemsCount={filteredItems.length}
                                isExtractionProject={isExtractionProject}
                                areItemsFiltered={Boolean(searchQuery) || areItemsFiltered()}
                                onRowClick={index => {
                                    setSelectedTableRowIndex(index);
                                    setIsSelectedFileModalHidden(false);
                                }}
                                isLoading={areDocumentsLoading || verificationDataTracker.isRunning}
                                onSortColumn={column =>
                                    setSortKey({
                                        key: column.key as keyof CustomTextTestSetDetailsTableRow,
                                        order: column.isSortedDescending ? 'asc' : 'desc'
                                    })
                                }
                            />
                        </ModelsTableWindow>

                        <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />
                    </div>
                )}

                {selectedTableRowIndex > -1 && pageItems.length > selectedTableRowIndex && (
                    <TestDocumentDetailsModal
                        tableRows={pageItems}
                        hidden={isSelectedFileModalHidden}
                        selectedModelName={selectedModelName}
                        isExtractionProject={isExtractionProject}
                        currentSelectedRowIndex={selectedTableRowIndex}
                        showIncorrectPredictions={showIncorrectPredictions}
                        onDismiss={() => setIsSelectedFileModalHidden(true)}
                    />
                )}

                <CustomTextTestModelsFiltersBlade
                    isOpen={!isFilterBladeHidden}
                    labelsToFilter={labelsToFilter}
                    onFiltersChange={updateFilteredItems}
                    resultTypesToFilter={resultsToFilter}
                    predictionsToFilter={predictionToFilter}
                    onDismiss={() => setIsFilterBladeHidden(true)}
                />
            </Stack>
        </Page>
    );
});
